"use client";

import axios from "axios";
import { FC, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";

import { useStyles } from "./styles";
import { QuizLeaderboardResponse, Quiz, QuizLobbyPlayer } from "@/types/quiz";
import { useAppSelector } from "@/redux/store";
import { useLocalStorage } from "@/hooks/useStorage";
import { getLeaderboardHeading } from "../helpers";
import Leaderboard from "@/components/quiz/leaderboard/Leaderboard";
import FactCheckIcon from "@/components/icons/FactCheck";

interface LeaderboardWrapperProps {
  quiz: QuizLeaderboardResponse | Quiz;
  isLeaderboardPage?: boolean;
  player?: QuizLobbyPlayer | null;
  maxLeaderboardItems?: number;
}

const LeaderboardWrapper: FC<LeaderboardWrapperProps> = ({
  quiz,
  isLeaderboardPage = false,
  player = null,
  maxLeaderboardItems,
}) => {
  const { classes } = useStyles({ isLeaderboardPage, quizType: quiz.quizType });
  const router = useRouter();
  const searchParams = useSearchParams();
  const lobbyUUID = searchParams?.get("lobby") || null;
  const title = getLeaderboardHeading(quiz.quizType, lobbyUUID);
  const user = useAppSelector((state) => state.user.user);
  const [storedResults, setStoredResults] = useLocalStorage("quizResults", []);
  const displayedLeaderboardItems =
    maxLeaderboardItems === undefined
      ? quiz.leaderboard.leaderboardItems
      : quiz.leaderboard.leaderboardItems.slice(0, maxLeaderboardItems);

  useEffect(() => {
    if (storedResults && storedResults.length !== 0 && user) {
      const reqBody = {
        userId: user.id,
        quizResults: storedResults,
      };

      const sendQuizResults = async () => {
        try {
          await axios.patch(
            `${process.env.NEXT_PUBLIC_API_URL}/quiz/results`,
            reqBody,
            {
              withCredentials: true,
            }
          );
          router.refresh();
        } catch (error) {
          console.error("Error posting quiz results:", error);
        }
      };

      sendQuizResults();
      setStoredResults([]);
    }
  }, [storedResults, setStoredResults, user]);

  return (
    <>
      <header className={classes.header}>
        {isLeaderboardPage && <h1 className={classes.title}>{quiz.title}</h1>}
        <div className={classes.subtitle}>
          <h2 className={classes.subtitleText}>{title}</h2>
          <FactCheckIcon />
        </div>
      </header>
      <div className={classes.leaderboard}>
        <Leaderboard
          leaderboardType="quiz"
          quizType={quiz.quizType}
          leaderboardItems={displayedLeaderboardItems}
          histogramBuckets={quiz.leaderboard.histogramBuckets}
          userScore={quiz.leaderboard.userScore}
          userResult={quiz.leaderboard.userResult}
          userPercentile={quiz.leaderboard.userPercentile}
          player={player}
        />
      </div>
    </>
  );
};

export default LeaderboardWrapper;
